import React, { useState, useCallback } from 'react';
import { Outlet, Navigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { Sidebar } from './Sidebar';
import { TopBar } from './TopBar';
import { MfaRequiredNotice } from './MfaRequiredNotice';
import { Spinner } from '../ui/Spinner';

export interface PageContextValue {
  setPageTitle: (title: string) => void;
  setSearchConfig: (config: SearchConfig | null) => void;
  searchValue: string;
}

export interface SearchConfig {
  placeholder: string;
  onSearch: (value: string) => void;
}

export const PageContext = React.createContext<PageContextValue>({
  setPageTitle: () => {},
  setSearchConfig: () => {},
  searchValue: '',
});

export const AppLayout: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth();
  const [pageTitle, setPageTitle] = useState('');
  const [searchConfig, setSearchConfig] = useState<SearchConfig | null>(null);
  const [searchValue, setSearchValue] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchValue(value);
      searchConfig?.onSearch(value);
    },
    [searchConfig],
  );

  const setSearchConfigAndReset = useCallback((config: SearchConfig | null) => {
    setSearchConfig(config);
    setSearchValue('');
  }, []);

  if (isLoading) {
    return (
      <div
        style={{
          height: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Spinner size={32} />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return (
    <PageContext.Provider
      value={{
        setPageTitle,
        setSearchConfig: setSearchConfigAndReset,
        searchValue,
      }}
    >
      <div className="admin-shell">
        <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
        {sidebarOpen && <button className="admin-sidebar-overlay" aria-label="Close navigation" onClick={() => setSidebarOpen(false)} />}
        <div className="admin-shell-main">
          <TopBar
            pageTitle={pageTitle}
            searchConfig={searchConfig}
            searchValue={searchValue}
            onSearchChange={handleSearchChange}
            onMenuClick={() => setSidebarOpen(true)}
          />
          <main className="admin-main-content">
            <MfaRequiredNotice />
            <Outlet />
          </main>
        </div>
      </div>
    </PageContext.Provider>
  );
};
